import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import {
  Copy,
  Dice5,
  LogOut,
  RotateCcw,
  Zap,
  Users,
  Lock,
  Unlock,
  Wifi,
  WifiOff,
  Check,
  Sparkles,
  Download,
  Hash,
  UsersRound,
  Plus,
  Minus,
  Timer,
  TimerOff,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSessionSocket } from "@/hooks/useSessionSocket";
import { useCountdown } from "@/hooks/useCountdown";

const statusMeta = {
  open: { label: "Accepting", bg: "#ECFDF5", text: "#047857", border: "#A7F3D0" },
  closed: { label: "Closed", bg: "#FEF3C7", text: "#B45309", border: "#FDE68A" },
  revealed: { label: "Revealed", bg: "#EEF2FF", text: "#4338CA", border: "#C7D2FE" },
};

const GROUP_COLORS = [
  "bg-indigo-100 text-indigo-800 border-indigo-200",
  "bg-emerald-100 text-emerald-800 border-emerald-200",
  "bg-amber-100 text-amber-800 border-amber-200",
  "bg-rose-100 text-rose-800 border-rose-200",
  "bg-sky-100 text-sky-800 border-sky-200",
  "bg-violet-100 text-violet-800 border-violet-200",
  "bg-teal-100 text-teal-800 border-teal-200",
  "bg-orange-100 text-orange-800 border-orange-200",
];

function csvEscape(v) {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCsv(session) {
  const mode = session.draw_mode || "numbers";
  const header = mode === "groups"
    ? ["Name", "Emoji", "Group #", "Group Name"]
    : ["Name", "Emoji", "Number"];
  const rows = session.students.map((s) => {
    if (mode === "groups") {
      const gname = s.group != null ? (session.group_names?.[s.group - 1] || `Group ${s.group}`) : "";
      return [s.name, s.emoji || "", s.group ?? "", gname];
    }
    return [s.name, s.emoji || "", s.number ?? ""];
  });
  const csv = [header, ...rows].map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `random-roster-${session.code}-${mode}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Draw config
  const [drawMode, setDrawMode] = useState("numbers"); // 'numbers' | 'groups'
  const [groupCount, setGroupCount] = useState(2);
  const [groupNamesDraft, setGroupNamesDraft] = useState(["", ""]);

  const joinUrl = useMemo(() => {
    if (!session) return "";
    return `${window.location.origin}/join?code=${session.code}`;
  }, [session]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/session");
      setSession(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Keep group names draft length in sync with count
  useEffect(() => {
    setGroupNamesDraft((prev) => {
      const next = prev.slice(0, groupCount);
      while (next.length < groupCount) next.push("");
      return next;
    });
  }, [groupCount]);

  const { connected } = useSessionSocket(session?.code, (msg) => {
    if (msg.type === "connected") setSession(msg.session);
    if (msg.type === "student_joined") {
      setSession((s) =>
        s
          ? {
              ...s,
              students: [
                ...s.students,
                { id: msg.student.id, name: msg.student.name, emoji: msg.student.emoji, number: null, group: null },
              ],
            }
          : s
      );
    }
    if (msg.type === "status") setSession((s) => (s ? { ...s, status: msg.status } : s));
    if (msg.type === "revealed") setSession(msg.session);
    if (msg.type === "session_reset") load();
    if (msg.type === "timer_started") {
      setSession((s) => (s ? { ...s, submissions_deadline: msg.submissions_deadline } : s));
    }
    if (msg.type === "timer_stopped" || msg.type === "timer_expired") {
      setSession((s) => (s ? { ...s, submissions_deadline: null } : s));
    }
  });

  const countdown = useCountdown(session?.submissions_deadline);

  const startTimer = async (seconds) => {
    setBusy(true);
    try {
      const { data } = await api.post("/session/timer", { seconds });
      setSession(data);
      toast.success(`Timer started · ${Math.floor(seconds / 60)}m ${seconds % 60}s`);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const stopTimer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/session/timer/stop");
      setSession(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleSubs = async () => {
    if (!session) return;
    setBusy(true);
    try {
      const { data } = await api.post("/session/submissions", { accepting: session.status !== "open" });
      setSession(data);
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const revealNumbers = async () => {
    if (!session?.students.length) return toast.error("No students have joined yet");
    setBusy(true);
    try {
      const { data } = await api.post("/session/reveal");
      setSession(data);
      toast.success("Numbers revealed on every phone");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const revealGroups = async () => {
    if (!session?.students.length) return toast.error("No students have joined yet");
    if (groupCount > session.students.length)
      return toast.error("More groups than students");
    setBusy(true);
    try {
      const { data } = await api.post("/session/groups", {
        count: groupCount,
        names: groupNamesDraft.map((n) => n.trim()),
      });
      setSession(data);
      toast.success("Groups revealed on every phone");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!confirm("Reset for a new class? All submissions will be cleared.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/session/reset");
      setSession(data);
      setDrawMode("numbers");
      toast.success("Fresh session ready");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleLogout = () => { logout(); navigate("/"); };

  if (loading) {
    return (
      <div className="min-h-screen paper-bg flex items-center justify-center">
        <div className="text-slate-500 font-semibold">Loading your classroom…</div>
      </div>
    );
  }
  if (!session) return null;
  const meta = statusMeta[session.status];
  const revealed = session.status === "revealed";
  const canExport = revealed && session.students.length > 0;

  return (
    <div className="min-h-screen paper-bg">
      <div className="border-b border-slate-200/60 bg-white/60 backdrop-blur sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center rotate-3">
              <Dice5 className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-display font-extrabold text-slate-900 leading-none">
                {user?.name || "Teacher"}
              </div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="pill"
              style={{ background: connected ? "#ECFDF5" : "#FEE2E2", color: connected ? "#047857" : "#B91C1C", border: `1px solid ${connected ? "#A7F3D0" : "#FECACA"}` }}
              data-testid="ws-status"
            >
              {connected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
              {connected ? "Live" : "Reconnecting"}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-1.5"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <div className="grid grid-cols-12 gap-6 lg:gap-8">
          {/* Left: QR */}
          <div className="col-span-12 lg:col-span-5 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-paper p-6 sm:p-8 relative"
            >
              <div className="tape left-10 -top-3" />
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600">
                    Join code
                  </div>
                  <div
                    className="font-mono-num text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter"
                    data-testid="session-code"
                  >
                    {session.code}
                  </div>
                </div>
                <span
                  className="pill"
                  style={{ background: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}
                  data-testid="session-status-badge"
                >
                  {session.status === "open" && (
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  )}
                  {meta.label}
                </span>
              </div>
              <div className="bg-white rounded-2xl border-2 border-slate-900/10 shadow-inner p-4 sm:p-6 flex items-center justify-center" data-testid="qr-code-display">
                <QRCodeSVG value={joinUrl} size={260} bgColor="#FFFFFF" fgColor="#1E293B" level="M" includeMargin={false} />
              </div>
              <div className="mt-5 flex gap-2">
                <div className="flex-1 min-w-0 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 truncate">
                  {joinUrl}
                </div>
                <button
                  onClick={copyLink}
                  data-testid="copy-session-link-button"
                  className="btn-secondary py-2 px-3 inline-flex items-center gap-1.5 text-sm"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Students scan the QR or type this URL. No accounts needed.
              </p>
            </motion.div>
          </div>

          {/* Right */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            {/* Roster */}
            <div className="card-paper p-6 sm:p-8">
              <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-600 flex items-center gap-2">
                    <Users className="w-3.5 h-3.5" /> Live roster
                  </div>
                  <div className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1">
                    <span data-testid="live-student-count-badge">{session.students.length}</span>{" "}
                    student{session.students.length === 1 ? "" : "s"} joined
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => window.open("/print", "_blank")}
                    disabled={!canExport}
                    data-testid="open-print-view-button"
                    className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-40"
                    title={canExport ? "Open printable view" : "Reveal a draw first to print"}
                  >
                    <Printer className="w-4 h-4" /> Print
                  </button>
                  <button
                    onClick={() => downloadCsv(session)}
                    disabled={!canExport}
                    data-testid="export-csv-button"
                    className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-1.5 disabled:opacity-40"
                    title={canExport ? "Download CSV" : "Reveal a draw first to export"}
                  >
                    <Download className="w-4 h-4" /> Export CSV
                  </button>
                </div>
              </div>

              {/* Group summary chips when revealed as groups */}
              {revealed && session.draw_mode === "groups" && (
                <div className="mb-4 flex flex-wrap gap-2" data-testid="groups-summary">
                  {(session.group_names || []).map((gn, i) => {
                    const count = session.students.filter((s) => s.group === i + 1).length;
                    const color = GROUP_COLORS[i % GROUP_COLORS.length];
                    return (
                      <span
                        key={`group-chip-${i}`}
                        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold border ${color}`}
                      >
                        <UsersRound className="w-3.5 h-3.5" />
                        {gn} · {count}
                      </span>
                    );
                  })}
                </div>
              )}

              {session.students.length === 0 ? (
                <div className="text-center py-14 border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/60">
                  <Sparkles className="w-8 h-8 text-indigo-400 mx-auto mb-3" />
                  <div className="font-display font-bold text-slate-800 text-lg">
                    Waiting for students…
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    Show the QR code on your projector so students can scan.
                  </div>
                </div>
              ) : (
                <div
                  className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[380px] overflow-y-auto thin-scroll pr-1"
                  data-testid="student-roster-grid"
                >
                  <AnimatePresence initial={false}>
                    {session.students.map((s, idx) => {
                      const groupColor = s.group != null
                        ? GROUP_COLORS[(s.group - 1) % GROUP_COLORS.length]
                        : "bg-white border-slate-200";
                      return (
                        <motion.div
                          key={s.id}
                          layout
                          initial={{ opacity: 0, y: 15, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.9 }}
                          transition={{ duration: 0.25 }}
                          data-testid={`student-card-${s.id}`}
                          className={`relative border-2 rounded-xl p-3 flex items-center gap-2 ${
                            s.group != null ? groupColor : "bg-white border-slate-200"
                          }`}
                          style={{ transform: `rotate(${(idx % 2 ? 1 : -1) * 0.6}deg)` }}
                        >
                          <div className="w-10 h-10 rounded-lg bg-white/70 flex items-center justify-center text-2xl shrink-0 border border-slate-200/70">
                            {s.emoji || s.name.slice(0, 1).toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm text-slate-800 truncate">
                              {s.name}
                            </div>
                            {s.number != null && (
                              <div className="text-xs font-mono-num font-bold text-indigo-600">
                                #{s.number}
                              </div>
                            )}
                            {s.group != null && (
                              <div className="text-[10px] font-bold uppercase tracking-wider truncate">
                                {session.group_names?.[s.group - 1] || `Group ${s.group}`}
                              </div>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Timer card */}
            <div className="card-paper p-5 sm:p-6" data-testid="timer-card">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Timer className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                      Join window timer
                    </div>
                    {countdown.active ? (
                      <div className="flex items-baseline gap-2">
                        <div
                          data-testid="timer-countdown"
                          className={`font-mono-num text-3xl font-black leading-none ${
                            countdown.remainingMs < 10000 ? "text-rose-600" : "text-slate-900"
                          }`}
                        >
                          {countdown.label}
                        </div>
                        <div className="text-xs text-slate-500">until submissions close</div>
                      </div>
                    ) : (
                      <div className="text-sm text-slate-600">
                        Set a countdown so students know how long they have to join.
                      </div>
                    )}
                  </div>
                </div>
                {countdown.active ? (
                  <button
                    onClick={stopTimer}
                    disabled={busy}
                    data-testid="timer-stop-button"
                    className="btn-secondary text-sm py-2 px-3 inline-flex items-center gap-1.5"
                  >
                    <TimerOff className="w-4 h-4" /> Stop timer
                  </button>
                ) : (
                  <div className="flex flex-wrap gap-2" data-testid="timer-presets">
                    {[30, 60, 120, 300].map((secs) => (
                      <button
                        key={secs}
                        onClick={() => startTimer(secs)}
                        disabled={busy || session.status !== "open"}
                        data-testid={`timer-start-${secs}`}
                        className="btn-secondary text-xs py-2 px-3 disabled:opacity-40"
                      >
                        {secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Draw controls */}
            <div className="card-paper p-5 sm:p-6">
              <div className="flex bg-slate-100 rounded-xl p-1 mb-4 max-w-xs" role="tablist">
                <button
                  type="button"
                  onClick={() => setDrawMode("numbers")}
                  data-testid="draw-mode-numbers"
                  className={`flex-1 py-2 text-sm font-bold rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors ${
                    drawMode === "numbers" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                  }`}
                >
                  <Hash className="w-4 h-4" /> Numbers
                </button>
                <button
                  type="button"
                  onClick={() => setDrawMode("groups")}
                  data-testid="draw-mode-groups"
                  className={`flex-1 py-2 text-sm font-bold rounded-lg inline-flex items-center justify-center gap-1.5 transition-colors ${
                    drawMode === "groups" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
                  }`}
                >
                  <UsersRound className="w-4 h-4" /> Groups
                </button>
              </div>

              {drawMode === "groups" && (
                <div className="mb-4 border-2 border-slate-200 rounded-xl p-4 bg-slate-50/60">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-800">Number of groups</div>
                      <div className="text-xs text-slate-500">
                        Students split as evenly as possible.
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1 bg-white border-2 border-slate-200 rounded-lg">
                      <button
                        type="button"
                        onClick={() => setGroupCount((c) => Math.max(2, c - 1))}
                        className="p-2 hover:bg-slate-50"
                        data-testid="group-count-decrease"
                        aria-label="Fewer groups"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <div
                        data-testid="group-count-value"
                        className="w-8 text-center font-mono-num font-bold text-lg"
                      >
                        {groupCount}
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setGroupCount((c) =>
                            Math.min(20, Math.max(2, Math.min(session.students.length || 20, c + 1)))
                          )
                        }
                        className="p-2 hover:bg-slate-50"
                        data-testid="group-count-increase"
                        aria-label="More groups"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="group-names-list">
                    {groupNamesDraft.map((val, i) => (
                      <input
                        key={`group-name-${i}`}
                        value={val}
                        onChange={(e) =>
                          setGroupNamesDraft((prev) => {
                            const next = [...prev];
                            next[i] = e.target.value;
                            return next;
                          })
                        }
                        data-testid={`group-name-input-${i}`}
                        className="px-3 py-2 rounded-lg border-2 border-slate-200 bg-white focus:border-indigo-500 focus:outline-none text-sm"
                        placeholder={`Group ${i + 1}`}
                        maxLength={40}
                      />
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-2">
                    Optional. Leave blank to use “Group 1”, “Group 2”, etc.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={toggleSubs}
                  disabled={busy || revealed}
                  data-testid="toggle-submissions-button"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  {session.status === "open" ? (
                    <><Lock className="w-4 h-4" /> Close submissions</>
                  ) : (
                    <><Unlock className="w-4 h-4" /> Open submissions</>
                  )}
                </button>
                {drawMode === "numbers" ? (
                  <button
                    onClick={revealNumbers}
                    disabled={busy || revealed || session.students.length === 0}
                    data-testid="randomize-reveal-button"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                  >
                    <Zap className="w-4 h-4" /> Randomize &amp; Reveal
                  </button>
                ) : (
                  <button
                    onClick={revealGroups}
                    disabled={busy || revealed || session.students.length === 0}
                    data-testid="randomize-groups-button"
                    className="btn-primary inline-flex items-center justify-center gap-2"
                  >
                    <Zap className="w-4 h-4" /> Randomize into Groups
                  </button>
                )}
                <button
                  onClick={reset}
                  disabled={busy}
                  data-testid="reset-session-button"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <RotateCcw className="w-4 h-4" /> Reset for next class
                </button>
              </div>
              {revealed && (
                <div className="mt-4 text-sm text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                  {session.draw_mode === "groups" ? "Groups" : "Numbers"} revealed on every student&apos;s phone. Export a CSV or reset when you&apos;re ready.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
